import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';

export default function WorkWithUsPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    businessName: '',
    category: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will review your application and contact you soon.');
    setFormData({ fullName: '', email: '', businessName: '', category: '', message: '' });
  };

  const benefits = [
    {
      title: 'Wide Customer Base',
      description: 'Access to thousands of potential customers across the region',
    },
    {
      title: 'Easy Management',
      description: 'Simple dashboard to manage inventory, orders, and analytics',
    },
    {
      title: 'Secure Payments',
      description: 'Safe and reliable payment processing with regular settlements',
    },
    {
      title: 'Marketing Support',
      description: 'Featured listings and promotional opportunities to boost sales',
    },
    {
      title: '24/7 Support',
      description: 'Dedicated support team to help with any issues or questions',
    },
    {
      title: 'Logistics Partner',
      description: 'Integrated shipping and delivery solutions for efficiency',
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5f6fb] flex flex-col">
      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-8 space-y-6">
        <nav className="text-xs text-gray-500 mb-2">
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>{' '}
          / <span className="text-gray-700 font-medium">Work with Us</span>
        </nav>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-[#fd9e10] to-[#f08c00] rounded-[32px] shadow-lg p-8 sm:p-12 text-white space-y-4">
          <h1 className="text-3xl font-bold">Grow Your Business with Dehleze</h1>
          <p className="text-base text-orange-50 max-w-2xl">
            Join our marketplace and reach thousands of customers. Whether you're a small business,
            established brand, or entrepreneur, Dehleze provides the platform and support you need to succeed.
          </p>
        </section>

        {/* Benefits Section */}
        <section className="bg-white rounded-[32px] shadow-lg border border-gray-100 p-6 sm:p-8 space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Why Partner with Dehleze?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="flex gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
              >
                <CheckCircle className="h-6 w-6 text-[#fd9e10] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Requirements Section */}
        <section className="bg-white rounded-[32px] shadow-lg border border-gray-100 p-6 sm:p-8 space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Requirements</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-4">For Individual Sellers</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Valid national ID or CNIC</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Active bank account for payments</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Contact information and address</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Product inventory and details</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-4">For Businesses</h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Business registration documents</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Tax identification number</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Branded product catalog</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#ff9100]">✓</span>
                  <span>Warehouse/fulfillment capability</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="bg-white rounded-[32px] shadow-lg border border-gray-100 p-6 sm:p-8 space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Apply Now</h2>
          <p className="text-sm text-gray-600">
            Fill out the form below and our team will review your application within 2-3 business days.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#fd9e10]"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#fd9e10]"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#fd9e10]"
                  placeholder="Your business name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#fd9e10]"
                >
                  <option value="">Select a category</option>
                  <option value="fashion">Fashion & Apparel</option>
                  <option value="electronics">Electronics</option>
                  <option value="home">Home & Living</option>
                  <option value="beauty">Beauty & Personal Care</option>
                  <option value="sports">Sports & Outdoors</option>
                  <option value="food">Food & Groceries</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#fd9e10]"
                placeholder="Tell us about your business and why you'd like to partner with Dehleze"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#fd9e10] text-white py-3 rounded-lg font-semibold hover:bg-[#f08c00] transition-colors"
            >
              Submit Application
            </button>
          </form>
        </section>

        {/* Contact Section */}
        <section className="bg-blue-50 rounded-[32px] border border-blue-200 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Have Questions?</h2>
          <p className="text-sm text-gray-600">
            Contact our partnership team at{' '}
            <a href="mailto:partners@dehleze.com" className="text-[#fd9e10] font-medium hover:underline">
              partners@dehleze.com
            </a>{' '}
            or call us at +92-300-1234567
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
